import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { AdapterDateFns } from "@mui/x-date-pickers/AdapterDateFns";
import { DateRange, DateRangePicker } from "@mui/x-date-pickers-pro";
import { isValid } from "date-fns";
import {
  Box,
  IconButton,
  InputAdornment,
  InputLabel,
} from "@mui/material";

//icon
import CloseOutlinedIcon from '@mui/icons-material/CloseOutlined';

//locales
import ruLocale from "date-fns/locale/ru";
import ukLocale from "date-fns/locale/uk";
import enLocale from "date-fns/locale/en-US";
import esLocale from "date-fns/locale/es";
import trLocale from "date-fns/locale/tr";
import koLocale from "date-fns/locale/ko";
import elLocale from "date-fns/locale/el";
import frLocale from "date-fns/locale/fr";
import ptLocale from "date-fns/locale/pt";

//styles
import { useStyles } from "./styles";

const localeMap = {
  en: enLocale,
  ru: ruLocale,
  uk: ukLocale,
  es: esLocale,
  tr: trLocale,
  ko: koLocale,
  el: elLocale,
  fr: frLocale,
  pt: ptLocale,
};

interface IMuiTwiceDateRangePickerProps {
  startDate: any;
  endDate: any;
  label: string;
  startLabel: string;
  endLabel: string;
  onChange: (start: Date | null, end: Date | null) => void;
  error?: any;
  hideLabel?: boolean;
  hideClear?: boolean;
  fieldClassName?: string;
  validateDates?: boolean;
  onValidationError?: (error: string, dates: { start: any, end: any }) => void;
}

const MuiTwiceDateRangePicker: React.FC<IMuiTwiceDateRangePickerProps> = ({
  startDate,
  endDate,
  startLabel,
  endLabel,
  onChange,
  error = null,
  label = 'common.forms.fields.custom_dates',
  hideLabel = false,
  hideClear = false,
  fieldClassName = '',
  validateDates = true,
  onValidationError,
}): JSX.Element => {
  const { t, i18n } = useTranslation();
  const styles = useStyles();

  const [locale, setLocale] = useState<object>(localeMap.en);
  const [validationErrors, setValidationErrors] = useState<{
    start: boolean;
    end: boolean;
  }>({ start: false, end: false });

  const [value, setValue] = React.useState<DateRange<Date>>(() => {
    const safeStartDate = startDate && isValid(new Date(startDate)) ? new Date(startDate) : null;
    const safeEndDate = endDate && isValid(new Date(endDate)) ? new Date(endDate) : null;
    return [safeStartDate, safeEndDate];
  });

  useEffect(() => {
    const safeStartDate = startDate && isValid(new Date(startDate)) ? new Date(startDate) : null;
    const safeEndDate = endDate && isValid(new Date(endDate)) ? new Date(endDate) : null;

    setValue(() => [
      safeStartDate,
      safeEndDate,
    ]);

    // Validate dates when props change
    const validation = validateDateRange(startDate, endDate);
    setValidationErrors({
      start: validation.startError,
      end: validation.endError
    });
  }, [startDate, endDate]);

  useEffect(() => {
    if (i18n.language === 'ru') {
      setLocale(localeMap.ru);
    }
    if (i18n.language === 'en') {
      setLocale(localeMap.en);
    }
    if (i18n.language === 'uk') {
      setLocale(localeMap.uk);
    }
    if (i18n.language === 'es') {
      setLocale(localeMap.es);
    }
    if (i18n.language === 'tr') {
      setLocale(localeMap.tr);
    }
    if (i18n.language === 'ko') {
      setLocale(localeMap.ko);
    }
    if (i18n.language === 'el') {
      setLocale(localeMap.el);
    }
    if (i18n.language === 'fr') {
      setLocale(localeMap.fr);
    }
    if (i18n.language === 'pt') {
      setLocale(localeMap.pt);
    }
  }, [i18n.language]);

  const validateDateRange = (start: any, end: any): {
    isValid: boolean;
    error?: string;
    startError: boolean;
    endError: boolean;
  } => {
    if (!validateDates) {
      return { isValid: true, startError: false, endError: false };
    }

    let startError = false;
    let endError = false;
    let error = '';

    // Validate start date
    if (start) {
      const startDate = new Date(start);
      if (!isValid(startDate)) {
        startError = true;
        error = 'Invalid start date provided';
      } else if (startDate.getFullYear() < 1970) {
        startError = true;
        error = error ? `${error}; Start date year cannot be less than 1970` : 'Start date year cannot be less than 1970';
      }
    }

    // Validate end date
    if (end) {
      const endDate = new Date(end);
      if (!isValid(endDate)) {
        endError = true;
        error = error ? `${error}; Invalid end date provided` : 'Invalid end date provided';
      } else if (endDate.getFullYear() < 1970) {
        endError = true;
        error = error ? `${error}; End date year cannot be less than 1970` : 'End date year cannot be less than 1970';
      }
    }

    // Check if start date is after end date
    if (start && end && !startError && !endError) {
      const startDate = new Date(start);
      const endDate = new Date(end);
      if (startDate > endDate) {
        startError = true;
        endError = true;
        error = error ? `${error}; Start date cannot be after end date` : 'Start date cannot be after end date';
      }
    }

    return {
      isValid: !startError && !endError,
      error: error || undefined,
      startError,
      endError
    };
  };

  const handleDateChange = (newValue: DateRange<Date>) => {
    const [start, end] = newValue;
    setValue(newValue);

    const validation = validateDateRange(start, end);

    // Update validation errors state
    setValidationErrors({
      start: validation.startError,
      end: validation.endError
    });

    if (!validation.isValid) {
      console.error('Date validation failed:', validation.error);

      if (onValidationError) {
        onValidationError(validation.error!, { start, end });
      }

      return;
    }

    const safeStart = start && isValid(start) ? start : null;
    const safeEnd = end && isValid(end) ? end : null;
    onChange(safeStart, safeEnd);
  };

  return (
    <Box className={styles.field}>
      <LocalizationProvider dateAdapter={AdapterDateFns} adapterLocale={locale}>
        {!hideLabel && (
          <InputLabel shrink={true} sx={{ marginBottom: '4px' }}>
            {t(label)}
          </InputLabel>
        )}
        <DateRangePicker
          className={`${fieldClassName}`}
          localeText={{ start: t(startLabel), end: t(endLabel) }}
          value={value}
          onChange={handleDateChange}
          maxDate={new Date()}
          slotProps={{
            textField: ({ position }) => ({
              size: 'small',
              error: position === 'start'
                ? (!!error || validationErrors.start)
                : validationErrors.end,
              InputProps: {
                endAdornment: (<>
                  {!hideClear && (<>
                    {position === 'start' ? (<>
                      {!!value[0] && (
                        <InputAdornment position="end">
                          <IconButton
                            className={'clear'}
                            size="small"
                            onClick={(e) => {
                              e.preventDefault();

                              setValue((prevState) => {
                                onChange(null, prevState[1]);
                                return [ null, prevState[1] ]
                              });

                            }}
                          >
                            <CloseOutlinedIcon fontSize="small" />
                          </IconButton>
                        </InputAdornment>
                      )}
                    </>) : (<>
                      {!!value[1] && (
                        <InputAdornment position="end">
                          <IconButton
                            className={'clear'}
                            size="small"
                            onClick={(e) => {
                              e.preventDefault();

                              setValue((prevState) => {
                                onChange(prevState[0], null);
                                return [ prevState[0], null ]
                              })
                            }}
                          >
                            <CloseOutlinedIcon fontSize="small" />
                          </IconButton>
                        </InputAdornment>
                      )}
                    </>)}
                  </>)}
                </>),
              },
            }),
            actionBar: ({ wrapperVariant }) => {
              return {
                actions: wrapperVariant === 'desktop' ? [] : ['accept'],
              }
            },
          }}
        />
      </LocalizationProvider>
    </Box>
  );
};

export default MuiTwiceDateRangePicker;
